import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { gettext } from '../../lib/i18n'
import Modal from './Modal'
import styles from './ConfirmDialog.module.css'

interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

interface ConfirmContextValue {
  confirm: ConfirmFn
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState<ConfirmOptions>({ message: '' })
  const resolverRef = useRef<((v: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  const close = useCallback((result: boolean) => {
    setOpen(false)
    resolverRef.current?.(result)
    resolverRef.current = null
  }, [])

  const { title, message, confirmText, cancelText, danger } = opts

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Modal
        open={open}
        onClose={() => close(false)}
        title={title ?? gettext('확인')}
        size="sm"
      >
        <p className={styles.message}>{message}</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={() => close(false)}
          >
            {cancelText ?? gettext('취소')}
          </button>
          <button
            type="button"
            className={
              danger ? `${styles.confirmBtn} ${styles.danger}` : styles.confirmBtn
            }
            onClick={() => close(true)}
          >
            {confirmText ?? gettext('확인')}
          </button>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider')
  return ctx
}
